/**
 * Width measurement step: review and correct every patch of the iteration.
 *
 * All patch groups appear in one grid. The operator sets each patch's two
 * edges by hand - the manual borders are the standard, automatic FWHM was
 * rejected - and those edges decide the classification: a patch without usable
 * edges is Unmarked or Burnt and carries the -1 sentinel instead of a width,
 * which buries it below every usable sample in the ranking.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Stack,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Divider,
  Typography,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import NavBarBottom, { navBottomButtonSx } from "../../components/NavBarBottom";
import { useExplorationParams } from "../../helper/entityRoutes";
import { EXPLORATION_LIST_ROUTE } from "../../helper/explorationRoutes";
import useIterationState from "../../helper/useIterationState";
import { parseConfigField } from "../../helper/explorationConfigHelpers";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import IterationExitDialog from "../../components/IterationExitDialog";
import ImageGrid from "../../components/ImageGrid";
import axios from "axios";
import {
  EXPLORATION_FLOW_STEPS,
  populationLabelForPhase,
} from "../../helper/colorExplorationParameters";
import { revertIterationWidthMeasurement } from "../../helper/iterationFlowControls";
import {
  WIDTH_PATCH_CACHE_KEY,
  buildWidthPatchCache,
  readWidthPatchCache,
  updateWidthPatchCacheEntry,
} from "../../helper/widthPatchCache";

const gridSectionPaperSx = {
  width: "100%",
  minWidth: 0,
  maxWidth: "100%",
  m: 0,
  boxSizing: "border-box",
  p: { xs: 2, md: 3 },
  borderRadius: 3,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
};

export default function ColorExplorationMeasureWidth() {
  // The exploration is the URL's; its patch-group grid size comes from its
  // plate design config.
  const { explorationId, stepRoute } = useExplorationParams();
  const [gridSize, setGridSize] = useState(null);
  const [redoMeasurement, setRedoMeasurement] = useState(false);
  const [restartBusy, setRestartBusy] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [markCollectionError, setMarkCollectionError] = useState(null);
  // True while we're polling api/patch/index/detail after a mark/collection
  // request appeared to time out. The backend may have actually finished;
  // without this recovery loop the user used to see a generic error and had
  // no way to tell whether retrying was safe.
  const [markCollectionRecoveryBusy, setMarkCollectionRecoveryBusy] = useState(false);
  const [markCollectionBusy, setMarkCollectionBusy] = useState(false);
  const [totalBatches, setTotalBatches] = useState(0);
  const [currentPatchIndex, setCurrentPatchIndex] = useState(0);
  const [measuredAll, setMeasuredAll] = useState(false);
  // The grid sits behind two slow async steps on first load:
  //   1. POST api/exploration/width/results/data -> backend classifies and
  //      measures every patch in the uploaded cluster image (the "patches
  //      are being classified" wait the user complained about).
  //   2. N parallel api/patch/file/get downloads to materialize each
  //      patch's image blob URL.
  // Without an explicit loading state the content area is just blank for
  // 30+ seconds with no indication that anything is happening.
  const [loadingPatches, setLoadingPatches] = useState(true);
  const [patchLoadError, setPatchLoadError] = useState(null);
  // The running iteration, fetched for the exploration (the sidebar shows
  // the user-facing number: P_0 and Q_0 both belong to iteration 0, only
  // Q_n bumps it to n; the backend is keyed by the internal number).
  const { iteration, iterationRef, applyIterationResponse } = useIterationState();
  const explorationIteration = iteration.display;
  const iterationPhase = iteration.phase;

  // const [showGrid, setShowGrid] = useState(false);
  const [imageObjectsDownloaded, setImageObjectsDownloaded] = useState({});
  const navigate = useNavigate();


  const prepare_image_data = async (downloaded_data) => {
    setLoadingPatches(true);
    setPatchLoadError(null);

    try {
      const imageArray = await Promise.all(downloaded_data.map(async (image_data) => {
        const image = await download_patch_image(image_data.cluster_patch_image);
        const coordinates = [image_data.edge_a_location, image_data.edge_b_location];
        const cluster_width = image_data.cluster_width;
        const row = image_data.row;
        const column = image_data.column;
        const width_pixel = image_data.width_pixel;
        const width_metric = image_data.width_metric;
        const method = image_data.method;
        const patch_type = image_data.patch_type;
        const batch_number = image_data.batch_number;

        return {
          image,
          coordinates,
          cluster_width,
          row,
          column,
          width_pixel,
          width_metric,
          method,
          patch_type,
          batch_number,
        };
      }));

      setImageObjectsDownloaded(imageArray);
    } catch (error) {
      console.error('Error fetching images:', error);
      setPatchLoadError({
        message:
          error?.message ||
          "Could not download one or more patch images from the cloud.",
      });
    } finally {
      setLoadingPatches(false);
    }
  };
  // Resolves to the server's current iteration number so callers can key the
  // patch cache off it.
  const getIterationNumber = async () => {
    const request_data = { "explorationId": explorationId };
    try {
      const response = await axios.post('api/exploration/iteration/number', request_data);
      return applyIterationResponse(response).number;
    } catch (error) {
      console.error('Error:', error);
      return iterationRef.current.number;
    }
  };

  const getGridSize = async () => {
    try {
      const explorationResponse = await axios.get('api/color/explorer/exploration/' + explorationId + "/");
      const config = parseConfigField(explorationResponse.data.plate_design_config);
      setGridSize(config?.patch_group_grid_size?.[0] ?? null);
    } catch (error) {
      console.error('Error loading the plate design config:', error);
    }
  };

  const getWidthMeasurementResults = async () => {
    const request_data = { "explorationId": explorationId ,"iterationNumber" : iterationRef.current.number};
    setLoadingPatches(true);
    setPatchLoadError(null);
    try {
      const response = await axios.post(
        'api/exploration/width/results/data',
        request_data
      );
      const results = response.data.results || [];
      localStorage.setItem(
        WIDTH_PATCH_CACHE_KEY,
        buildWidthPatchCache(
          explorationId,
          iterationRef.current.number,
          results
        )
      );
      // prepare_image_data toggles loadingPatches off in its own finally,
      // so we don't need to clear it here on the success path.
      await prepare_image_data(results);
    } catch (error) {
      console.error('Error:', error);
      setPatchLoadError({
        message:
          error?.response?.data?.message ||
          error?.message ||
          "The backend could not classify the cluster image.",
      });
      setLoadingPatches(false);
    }
  };

  const getPatchDetails = async () => {
    const request_data = { 
        "explorationId": explorationId,
        "iterationNumber" : iterationRef.current.number,
        "isWidthPatchGroup" : true

     };
    axios.post('api/patch/index/detail', request_data)
        .then(response => {
            setTotalBatches(response.data.totalBatchCount);
            setCurrentPatchIndex(response.data.currentIndex);
            setMeasuredAll(response.data.measured);
        })
        .catch(error => {
            console.error('Error:', error);
        });
}

  useEffect(() => {
    let cancelled = false;
    getGridSize();
    (async () => {
      // Resolve the current iteration FIRST: the cache is only reusable if it
      // was stamped for this exploration and this iteration.
      const iterationNumber = await getIterationNumber();
      if (cancelled) return;
      getPatchDetails();

      // An unstamped or mismatched payload (e.g. left over from a previous
      // exploration or iteration) would otherwise be replayed verbatim and
      // every patch image would 404 with "Could not load patch data".
      const cached = readWidthPatchCache(
        localStorage.getItem(WIDTH_PATCH_CACHE_KEY),
        explorationId,
        iterationNumber
      );
      if (cached === null) {
        localStorage.removeItem(WIDTH_PATCH_CACHE_KEY);
        getWidthMeasurementResults();
      } else {
        prepare_image_data(cached);
      }
    })();
    return () => {
      cancelled = true;
    };
    
  }, []);

  const download_patch_image = async (cloud_file_path) => {

    try {
      const data = {
        fileDestination: cloud_file_path
      };
      const response = await axios.post('api/patch/file/get', data, {
        responseType: 'blob',
      });
      const fileUrl = URL.createObjectURL(response.data);
      return fileUrl;
    } catch (error) {
      console.error('Error:', error);
      throw error; // Re-throw the error to be caught by the caller
    }
  };

  const updateGridCellContent = (coordinates, cluster_width, row, column, width_pixel, width_metric, method, patch_type, index) => {
    const updatedImageObjects = [...imageObjectsDownloaded];
    const image_data = updatedImageObjects[index];
    if (image_data) {
      // Update the specific cell in the copied array
      updatedImageObjects[index] = {
        ...image_data,
        coordinates,
        cluster_width,
        row,
        column,
        width_pixel,
        width_metric,
        method,
        patch_type
      };
      // Set the state with the modified array
      setImageObjectsDownloaded(updatedImageObjects);
      // The page re-renders from this cache on every mount, so the correction
      // has to land there too — otherwise reopening the step replays the
      // automatic measurement and the manual edit looks lost.
      const updatedCache = updateWidthPatchCacheEntry(
        localStorage.getItem(WIDTH_PATCH_CACHE_KEY),
        index,
        {
          edge_a_location: coordinates && coordinates.length === 2 ? coordinates[0] : -1,
          edge_b_location: coordinates && coordinates.length === 2 ? coordinates[1] : -1,
          cluster_width,
          width_pixel,
          width_metric,
          method,
          patch_type,
        }
      );
      if (updatedCache !== null) {
        localStorage.setItem(WIDTH_PATCH_CACHE_KEY, updatedCache);
      }
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /**
   * Poll the exploration's patch-group state until the FULL_CLUSTER marking
   * pass has actually landed. Used after a mark/collection request appears
   * to time out at the proxy: the backend can still be running (the call
   * generates one set of marking files per batch + a zipped archive), so
   * we don't want to throw a hard error if it's about to finish anyway.
   *
   * Returns true when `needsWidthReviewBeforeColor` flips to false (i.e.
   * every batch in the iteration is now FULL_CLUSTER_MARKED).
   */
  const pollMarkCollectionCompletion = async ({ attempts = 24, intervalMs = 5000 } = {}) => {
    for (let i = 0; i < attempts; i++) {
      try {
        const statusResp = await axios.post("api/patch/index/detail", {
          explorationId: explorationId,
          iterationNumber: iterationRef.current.number,
          isWidthPatchGroup: false,
        });
        const data = statusResp?.data ?? {};
        if (data.needsWidthReviewBeforeColor === false) {
          return true;
        }
        if (data.explorationStatus === "FULL_CLUSTER_MARKED") {
          return true;
        }
      } catch (pollError) {
        console.warn("pollMarkCollectionCompletion: status check failed", pollError);
      }
      await sleep(intervalMs);
    }
    return false;
  };

  const runMarkCollectionAndAdvance = async (navigatePageName) => {
    setMarkCollectionBusy(true);
    setMarkCollectionError(null);
    try {
      try {
        await axios.post('api/mark/collection', {
          "explorationId": explorationId,
          "patchType": "FULL_CLUSTER",
        });
      } catch (error) {
        // Do NOT silently advance to the color step on a failed marking pass.
        // The color step depends on FULL_CLUSTER Patch rows and a generated
        // marking-files zip; if mark/collection fails, both can be missing,
        // which previously stranded the iteration with a misleading
        // "Patch matching query does not exist" error several steps later.
        console.error('Error: api/mark/collection failed:', error);
        const status = error?.response?.status ?? null;
        const code = error?.response?.data?.code ?? null;
        const detail =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Unknown error";
        const isTimeoutOrNetwork =
          error?.code === "ECONNABORTED" ||
          /Network Error/i.test(error?.message ?? "") ||
          status === 504 ||
          status === 502;

        if (isTimeoutOrNetwork) {
          // mark_collection is idempotent, so the safe move on a 504 is to
          // wait for the backend to finish. Surface the error only if the
          // poll never confirms completion.
          setMarkCollectionRecoveryBusy(true);
          const finished = await pollMarkCollectionCompletion();
          setMarkCollectionRecoveryBusy(false);
          if (finished) {
            navigate(navigatePageName);
            return;
          }
        }
        setMarkCollectionError({
          flavour: isTimeoutOrNetwork ? "timedOut" : "server",
          status,
          code,
          message: String(detail),
        });
        return;
      }
      navigate(navigatePageName);
    } finally {
      setMarkCollectionBusy(false);
    }
  };

  const nextPageClickFunc = async (navigatePageName) => {
    /*
     * `measuredAll` is fetched once on mount, so by the time the user clicks Next it
     * can be stale. Re-check the backend so:
     *   - all patch groups measured -> advance to Color measurement (step 3)
     *   - more patch groups remain  -> loop back to Geometric (step 1) for the next group
     */
    let allMeasured = measuredAll;
    let nextBatchIndex = currentPatchIndex;
    let totalCount = totalBatches;
    try {
      const statusResponse = await axios.post('api/patch/index/detail', {
        "explorationId": explorationId,
        "iterationNumber": iterationRef.current.number,
        "isWidthPatchGroup": true,
      });
      allMeasured = Boolean(statusResponse?.data?.measured);
      setMeasuredAll(allMeasured);
      if (statusResponse?.data?.totalBatchCount !== undefined) {
        totalCount = statusResponse.data.totalBatchCount;
        setTotalBatches(totalCount);
      }
      if (statusResponse?.data?.currentIndex !== undefined) {
        nextBatchIndex = statusResponse.data.currentIndex;
        setCurrentPatchIndex(nextBatchIndex);
      }
    } catch (error) {
      console.error('Error refreshing patch details before Next:', error);
    }

    if (!allMeasured) {
      navigate(stepRoute("resolution"));
      return;
    }

    await runMarkCollectionAndAdvance(navigatePageName);
  };

  const handleCloseMarkCollectionError = () => {
    setMarkCollectionError(null);
  };

  const handleRetryMarkCollection = async () => {
    setMarkCollectionError(null);
    await runMarkCollectionAndAdvance(stepRoute("color"));
  };

  const handleOpenRedoMeasurementDialog = () => {
    setRedoMeasurement(true);
  };

  const handleCloseRedoMeasurementDialog = () => {
    if (restartBusy) return;
    setRedoMeasurement(false);
  };

  /**
   * "Restart iteration" — the user spotted a wrong cluster photograph
   * (or the measured widths look off) and wants to start the cluster
   * step over for this iteration. We revert *every* patch group's width
   * measurement (no fragile current-batch-minus-one math, which used to
   * pick the wrong batch when all batches were already measured) and
   * route back to Geometric Measurement so they can re-upload images
   * from a clean slate.
   */
  const handleConfirmRedoMeasurement = async () => {
    setRestartBusy(true);
    try {
      await revertIterationWidthMeasurement(
        explorationId,
        iterationRef.current.number
      );
    } catch (error) {
      console.error("Restart cluster measurement failed:", error);
    } finally {
      setRestartBusy(false);
      setRedoMeasurement(false);
      navigate(stepRoute("resolution"));
    }
  };

  /**
   * Cancel used to exit silently, which meant that an iteration measured
   * from a wrong cluster photograph could be advanced to Color before the
   * user noticed. Now we open the iteration-exit dialog so the user has
   * to either continue editing or explicitly discard the iteration's
   * width measurements before leaving.
   */
  const handleOpenCancelDialog = () => {
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
  };

  const handleDiscardAndExit = async () => {
    try {
      await revertIterationWidthMeasurement(
        explorationId,
        iterationRef.current.number
      );
    } catch (error) {
      console.error("Discard cluster measurement failed:", error);
    }
    setCancelDialogOpen(false);
    navigate(EXPLORATION_LIST_ROUTE);
  };

  const measuredCount = Math.min(currentPatchIndex, totalBatches);
  const cancelSummary =
    totalBatches > 0
      ? measuredAll
        ? `All ${totalBatches} cluster patch group(s) in this iteration have been measured.`
        : `You've measured ${measuredCount} of ${totalBatches} cluster patch group(s) in this iteration.`
      : "";

  const nextLabel = measuredAll ? "Continue to Color Measurement" : "Return to Geometric Measurement";

  return (
    <Stack
      alignItems="center"
      sx={{
        width: "100%",
        pt: { xs: 4, sm: 5 },
        pb: 4,
        boxSizing: "border-box",
      }}
      spacing={0}
    >
      <ExplorationPageShell
        title="Width measurement"
        subtitle="Review and adjust width for every patch in this iteration. All patch groups appear in one grid."
        noPaper
        stepSidebar={{
          steps: EXPLORATION_FLOW_STEPS,
          currentIndex: 1,
          iterationValue: explorationIteration,
          populationLabel: populationLabelForPhase(
            iterationPhase,
            explorationIteration
          ),
        }}
        contentBoxSx={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          px: 0,
          py: { xs: 2, md: 3 },
        }}
      >
        <Stack spacing={3} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, alignItems: "stretch" }}>
          {loadingPatches ? (
            <Paper elevation={0} sx={gridSectionPaperSx}>
              <Stack
                alignItems="center"
                spacing={2}
                sx={{ py: { xs: 5, md: 7 }, px: 2, textAlign: "center" }}
              >
                <CircularProgress size={42} thickness={4} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Loading patch measurements…
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: 540 }}
                >
                  Fetching every classified patch from the cloud so you can
                  review and adjust their widths. This usually takes a few
                  seconds per batch — please don't refresh the page.
                </Typography>
              </Stack>
            </Paper>
          ) : patchLoadError ? (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => getWidthMeasurementResults()}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Retry
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Could not load patch data
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {patchLoadError.message}
              </Typography>
            </Alert>
          ) : imageObjectsDownloaded != null && imageObjectsDownloaded.length > 0 ? (
            <Paper elevation={0} sx={gridSectionPaperSx}>
              <ImageGrid
                imageObjects={imageObjectsDownloaded}
                gridSize={gridSize}
                updateGridCellContent={updateGridCellContent}
                explorationId={explorationId}
                batchNumber={currentPatchIndex}
              />
            </Paper>
          ) : (
            <Alert severity="info">
              No patches available for this iteration yet. If you just
              uploaded the cluster image, give the backend a moment and
              refresh.
            </Alert>
          )}

          <Divider sx={{ width: "100%" }} />

          <Box sx={{ width: "100%", pt: 1 }}>
            <NavBarBottom
              variant="embedded"
              prevPage={stepRoute("resolution")}
              nextPage={stepRoute("color")}
              enableNextPage={false}
              nextPageClickFunc={nextPageClickFunc}
              nextLabel={nextLabel}
              leadingSlot={
                <Button
                  variant="outlined"
                  color="primary"
                  disableElevation
                  sx={navBottomButtonSx}
                  onClick={handleOpenCancelDialog}
                >
                  Cancel
                </Button>
              }
              leftSlot={
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<ReplayIcon />}
                  sx={navBottomButtonSx}
                  onClick={handleOpenRedoMeasurementDialog}
                >
                  Restart iteration
                </Button>
              }
            />
          </Box>
        </Stack>
      </ExplorationPageShell>

      <Dialog
        open={redoMeasurement}
        onClose={handleCloseRedoMeasurementDialog}
        aria-labelledby="width-redo-dialog-title"
        aria-describedby="width-redo-dialog-description"
      >
        <DialogTitle id="width-redo-dialog-title">Restart iteration?</DialogTitle>
        <DialogContent>
          <DialogContentText id="width-redo-dialog-description">
            This discards the cluster width measurements for{" "}
            <strong>every patch group in this iteration</strong> and takes
            you back to Geometric Measurement so you can upload fresh
            microscope photos. Use this if the cluster image you uploaded
            was wrong or the measured widths look incorrect.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseRedoMeasurementDialog}
            disabled={restartBusy}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRedoMeasurement}
            variant="contained"
            color="error"
            autoFocus
            disabled={restartBusy}
            sx={{ textTransform: "none" }}
          >
            {restartBusy ? "Reverting…" : "Restart iteration"}
          </Button>
        </DialogActions>
      </Dialog>

      <IterationExitDialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        onDiscardAndExit={handleDiscardAndExit}
        summary={cancelSummary}
        stepLabel="this iteration's cluster width measurements"
        discardLabel="Discard width measurements and exit"
      />

      <Dialog
        open={Boolean(markCollectionError) || markCollectionRecoveryBusy}
        onClose={markCollectionRecoveryBusy ? undefined : handleCloseMarkCollectionError}
        aria-labelledby="mark-collection-error-dialog-title"
        aria-describedby="mark-collection-error-dialog-description"
      >
        <DialogTitle id="mark-collection-error-dialog-title">
          {markCollectionRecoveryBusy
            ? "Marking still in progress…"
            : markCollectionError?.flavour === "timedOut"
              ? "Marking step timed out"
              : "Marking step failed"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="mark-collection-error-dialog-description" component="div">
            {markCollectionRecoveryBusy ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">
                  The marking request hit the proxy timeout. Checking whether the
                  backend has finished. The iteration is not lost — this can take
                  up to two minutes for large grids.
                </Typography>
              </Stack>
            ) : markCollectionError?.flavour === "timedOut" ? (
              <Typography variant="body2" sx={{ mb: 1 }}>
                The FULL_CLUSTER marking pass took longer than the proxy
                timeout and we couldn't confirm completion. The marking step
                is idempotent: clicking Retry will re-run only the batches
                that haven't been finalized yet.
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ mb: 1 }}>
                The FULL_CLUSTER marking pass could not be completed
                {markCollectionError?.status ? ` (HTTP ${markCollectionError.status})` : ""}.
                The color step has not been started, because it depends on
                the marking output. Resolve the underlying error and click
                Retry — the step is safe to re-run.
              </Typography>
            )}
            {markCollectionError?.message ? (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", mt: 1 }}
              >
                {markCollectionError.status ? `HTTP ${markCollectionError.status}: ` : ""}
                {markCollectionError.message}
                {markCollectionError.code ? ` (${markCollectionError.code})` : ""}
              </Typography>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, pb: 2 }}>
          <Button
            onClick={handleCloseMarkCollectionError}
            color="inherit"
            disabled={markCollectionRecoveryBusy}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
          <Button
            onClick={handleRetryMarkCollection}
            variant="contained"
            color="primary"
            disabled={markCollectionBusy || markCollectionRecoveryBusy}
            sx={{ textTransform: "none" }}
          >
            {markCollectionBusy || markCollectionRecoveryBusy ? "Working…" : "Retry"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
